import { Component, OnDestroy, OnInit } from '@angular/core';
import {Localiser} from "../../services/localiser";
import {UserService} from "../../services/user.service";
import {proyecto} from "../../models/proyecto";
import {ZonaService} from "../../services/zona.service";
import {ConsumoService} from "../../services/consumo.service";
import {Chart, registerables} from "chart.js";
import {MatDialog} from "@angular/material/dialog";
import {NewEmpresaComponent} from "../users/new-empresa/new-empresa.component";
import {empresa} from "../../models/empresa";
import {selected, SelectionPrintComponent} from "./selection-print/selection-print.component";
import {ConfigureComponent} from "./configure/configure.component";
import {Subscription} from "rxjs";

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.css']
})
export class ReportsComponent implements OnInit, OnDestroy {

  constructor(public localiser: Localiser, public user: UserService, public zonaService: ZonaService,
              public consumoService: ConsumoService, public matDialog: MatDialog) {
    Chart.register(...registerables);
  }

  proyectsClicked: boolean[] = [];
  zonas = [];
  charts = [];

  monthSelected = undefined;
  months = [];
  showMonths = [];
  yearSelected = undefined;
  years = [];

  monthlyReportCheck: boolean = false;
  consumoTeorico = [];

  private subscriptions: Subscription[] = [];
  private destroyed: boolean = false;


  // https://www.epa.gov/energy/greenhouse-gas-equivalencies-calculator
  factors = {
    // Asumimos que una luminaria consume 0.8kWh -> 19.2 por 24h
    luminariaConsumoPorHora: 0.075,
    // https://www.epa.gov/energy/greenhouse-gases-equivalencies-calculator-calculations-and-references#kilowatt
    kwhToReducedTonCO2: 0.000709,
    kwhToConsumedTonCO2: 0.000433,
    co2ToKilometer: 0.0002504,
    co2ToHouse: 7.94,
    co2ToTree: 0.06,
    co2ToPhone: 0.00000822
  }

  ngOnInit(): void {
    for (let proyect of this.user.getProyectos()) {
      this.proyectsClicked[this.user.getProyectos().indexOf(proyect)] = false;
      this.zonas[this.user.getProyectos().indexOf(proyect)] = [];
    }
    if (this.user.getProyectos().length == 1) {
      this.proyectsClicked[0] = true;
    }
    this.setDateArrays();
    this.getFactoresConsumo();
    this.getConsumoTeorico();
    this.getChecks();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    // Sin esto las peticiones en curso siguen pintando sobre canvas que ya no existen.
    for (let subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions = [];
    // Chart.js mantiene una referencia a cada chart, así que hay que destruirlos
    // al salir del componente para liberar los canvas.
    for (let tuple of this.charts) {
      tuple.chart.destroy();
    }
    this.charts = [];
  }

  private track(subscription: Subscription) {
    this.subscriptions = this.subscriptions.filter(sub => !sub.closed);
    this.subscriptions.push(subscription);
  }

  getFactoresConsumo() {
    if (this.user.getProyectos()[0]?.idEmpresa != undefined) {
      this.track(this.consumoService.getLastFactoresConsumo(this.user.getProyectos()[0].idEmpresa).subscribe((result: any) => {
        this.factors = result;
      }, error => {
        // 404: la empresa todavía no tiene factores guardados y tampoco existe el
        // registro por defecto (id_empresa = '-1'), así que se usan los de este componente.
      }))
    }
  }

  getConsumoTeorico() {
    for (let proyecto of this.user.getProyectos()) {
      this.track(this.consumoService.getConsumoTeoricoByIdProyecto(proyecto.idProyecto).subscribe((result: number) => {
        let consumo = {idProyecto: proyecto.idProyecto, consumo: result};
        this.consumoTeorico.push(consumo);
      }, error => {
        let consumo = {idProyecto: proyecto.idProyecto, consumo: 0};
        this.consumoTeorico.push(consumo);
      }));
    }
  }

  getZonas() {
    this.user.getProyectos().forEach((proyecto, i) => {
      this.track(this.zonaService.getByIdProyecto(proyecto.idProyecto).subscribe((result: any) => {
        result.forEach(zona => {
          zona.consumo = null;
          this.getMonthlyByIdZona(zona, proyecto)
        })
        this.zonas[this.user.getProyectos().indexOf(proyecto)] = result;
        this.getConsumoByZonas(this.zonas);
      }));
    });
  }

  setDateArrays() {
    this.months = ["month.january", "month.february", "month.march", "month.april", "month.may", "month.june",
      "month.july", "month.august", "month.september", "month.october", "month.november", "month.december"];
    this.years = [];
    for (let i = 2022; i <= new Date().getFullYear(); i++) {
      this.years.push(i);
    }
    this.setActualDateFilter();
  }

  setActualDateFilter() {
    let date: Date = new Date();
    this.monthSelected = date.getMonth();
    this.selectYear(date.getFullYear() - 2022);
  }

  selectMonth(month) {
    this.monthSelected = month;
    this.getZonas();
  }

  selectYear(year) {
    this.yearSelected = year;
    if (year + 2022 == new Date().getFullYear()) {
      this.showMonths = this.months.slice(0, new Date().getMonth() + 1);
      if (this.monthSelected > (this.showMonths.length - 1)) {
        this.monthSelected = this.showMonths.length - 1;
      }
    } else {
      this.showMonths = this.months
    }
    this.getZonas();
  }

  getYearSelected() {
    return (this.yearSelected + 2022);
  }

  isActualMonth() {
    return this.yearSelected + 2022 == new Date().getFullYear() && this.monthSelected == new Date().getMonth();
  }

  openProyect(proyect: proyecto) {
    this.proyectsClicked[this.user.getProyectos().indexOf(proyect)] = !this.proyectsClicked[this.user.getProyectos().indexOf(proyect)];
  }

  getConsumoByZonas(zonas) {
    for (let zonasArray of zonas) {
      for (let zona of zonasArray) {
        this.track(this.consumoService.getByIdZona(zona.id).subscribe(result => {
          zona.consumo = result;
        }));
      }
    }
  }

  consumoLoaded(proyecto) {
    let bool = false;
    this.zonas[this.user.getProyectos().indexOf(proyecto)].forEach(zona => {
      if (zona.consumo != null) {
        bool = true;
      }
    });
    return bool;
  }

  consumoMonthZona(zona) {
    if (this.isActualMonth()) {
      return zona.consumo.month;
    } else {
      return this.getConsumoTotalByZona(zona);
    }
  }

  consumoMedio(number) {
    if (number == 0) {
      return 0;
    }
    if (this.isActualMonth()) {
      return (number / new Date(Date.now()).getDate()).toFixed(2);
    } else {
      let days: number = +((new Date().setFullYear(this.yearSelected + 2022, this.monthSelected + 1)
        - new Date().setFullYear(this.yearSelected + 2022, this.monthSelected))
        / 86400000).toFixed(0);
      return (number / days).toFixed(2);
    }
  }

  getMonthlyByIdZona(zona, proyecto) {
    this.track(this.consumoService.getMonthlyByIdZona(zona.id, this.monthSelected, this.yearSelected + 2022).subscribe(result => {
      zona.consumoMonthly = result;
      this.chargeChartZona(zona);
      this.chargeChartProyecto(proyecto)
    }));
  }

  consumoSumaProyecto(proyecto) {
    let consumo: number[] = [0, 0, 0];
    if (this.consumoLoaded(proyecto)) {
      if (this.isActualMonth()) {
        this.zonas[this.user.getProyectos().indexOf(proyecto)].forEach(zona => {
          if (zona.consumo != null)
            consumo = [consumo[0] + (zona.consumo.today / 1000), consumo[1] + (zona.consumo.week / 1000), consumo[2] + (zona.consumo.month / 1000)];
        });
      } else {
        this.zonas[this.user.getProyectos().indexOf(proyecto)].forEach(zona => {
          if (zona.consumoMonthly != null)
            consumo = [0, 0, consumo[2] + this.getConsumoTotalByZona(zona)];
        });
      }
    }
    return consumo;
  }

  getConsumoTotalByZona(zona) {
    let sum = 0;
    zona.consumoMonthly.consumptions.forEach(cons => sum += cons.consumption);
    return sum / 1000;
  }

  getMediaDiariaOfConsumoTotalByProyecto(proyecto) {
    let sum = 0;
    this.zonas[this.user.getProyectos().indexOf(proyecto)].forEach(zona => {
      let n = this.getMediaDiariaOfConsumoTotal(zona);
      sum += +this.consumoMedio(this.getConsumoTotalByZona(zona));
    });
    return sum.toFixed(2);
  }

  getMediaDiariaOfConsumoTotal(zona) {
    return (this.getConsumoTotalByZona(zona) / zona.consumoMonthly.consumptions.length).toFixed(2);
  }

  getDiaMasConsumoByProyecto(proyecto) {
    let dailyConsumption = this.sumConsumosDiarioByProyecto(proyecto);
    let max = Math.max(...dailyConsumption);
    let dia = dailyConsumption.indexOf(max) + 1;
    return dia + "/" + (this.monthSelected + 1) + " - " + (max / 1000).toFixed(2) + " kWh";
  }

  sumConsumosDiarioByProyecto(proyecto) {
    let dailyConsumption = [];
    this.zonas[this.user.getProyectos().indexOf(proyecto)].forEach(zona => {
      if (zona.consumoMonthly?.consumptions != undefined) {
        zona.consumoMonthly.consumptions.forEach(cons => {
          if (dailyConsumption.length >= cons.date) {
            dailyConsumption[cons.date - 1] += cons.consumption;
          } else {
            dailyConsumption.push(cons.consumption);
          }
        });
      }
    });
    return dailyConsumption;
  }

  getDiaMasConsumoByZona(zona) {
    let dia = {date: 0, consumption: 0};
    zona.consumoMonthly.consumptions.forEach(cons => {
      if (cons.consumption > dia.consumption) {
        dia = cons;
      }
    });
    return dia.date + "/" + (this.monthSelected + 1) + " - " + (dia.consumption / 1000).toFixed(2) + " kWh";
  }

  getConsumoCO2ByProyecto(proyecto) {
    return (this.consumoSumaProyecto(proyecto)[2] * this.factors.kwhToConsumedTonCO2).toFixed(2);
  }

  getConsumoCO2ByZona(zona) {
    return (this.getConsumoTotalByZona(zona) * this.factors.kwhToConsumedTonCO2).toFixed(2);
  }

  getAhorroConsumoByProyecto(proyecto) {
    if (this.zonas[this.user.getProyectos().indexOf(proyecto)].length > 0) {
      let numLuminarias = 0;
      this.zonas[this.user.getProyectos().indexOf(proyecto)].forEach(zona => {
        numLuminarias += zona.consumo.numLuminarias;
      });

      return (this.consumoTeorico.find(pryt => pryt.idProyecto == proyecto.idProyecto).consumo * 24
        * this.zonas[this.user.getProyectos().indexOf(proyecto)][0].consumoMonthly.consumptions.length)
      - this.consumoSumaProyecto(proyecto)[2];

    } else {
      return 0;
    }
  }

  getAhorroCO2ByProyecto(proyecto) {
    return (this.getAhorroConsumoByProyecto(proyecto) * this.factors.kwhToReducedTonCO2).toFixed(2);
  }

  getKilometrosCocheByProyecto(proyecto) {
    return (this.getAhorroConsumoByProyecto(proyecto) * (this.factors.kwhToReducedTonCO2) / (this.factors.co2ToKilometer)).toFixed(2);
  }

  getEnergiaCasaByProyecto(proyecto) {
    return (this.getAhorroConsumoByProyecto(proyecto) * (this.factors.kwhToReducedTonCO2) / (this.factors.co2ToHouse)).toFixed(2);
  }

  getArbolesPlantadosByProyecto(proyecto) {
    return (this.getAhorroConsumoByProyecto(proyecto) * (this.factors.kwhToReducedTonCO2) / (this.factors.co2ToTree)).toFixed(0);
  }

  getMovilesCargadosByProyecto(proyecto) {
    return (this.getAhorroConsumoByProyecto(proyecto) * (this.factors.kwhToReducedTonCO2) / (this.factors.co2ToPhone)).toFixed(0);
  }

  canvasDynamicIdProyecto(proyecto) {
    return 'canvasId-' + proyecto.idProyecto;
  }

  canvasDynamicIdZona(zona) {
    return 'canvasId-' + zona.id;
  }

  printDynamicId(proyecto: proyecto) {
    return 'print-' + proyecto.nombre;
  }

  chargeChartZona(zona) {
    let labels = [];
    let data = [];
    for (let consumoDaily of zona.consumoMonthly.consumptions) {
      labels.push(consumoDaily.date);
      data.push(consumoDaily.consumption);
    }
    this.chargeChart("canvasId-" + zona.id, labels, data);
  }

  chargeChartProyecto(proyecto) {
    let dailyConsumptions = this.sumConsumosDiarioByProyecto(proyecto);
    let labels = [...Array(dailyConsumptions.length).keys()].map(x => x + 1);
    this.chargeChart("canvasId-" + proyecto.idProyecto, labels, dailyConsumptions);
  }

  chargeChart(idChart, labels, data, retry = true) {
    if (this.destroyed) {
      return;
    }
    if (document.getElementById(idChart) == null) {
      // La respuesta ha llegado antes de que Angular renderice el canvas: se
      // reintenta una vez tras el siguiente render y si no está, no se pinta.
      if (retry) {
        setTimeout(() => this.chargeChart(idChart, labels, data, false));
      }
      return;
    }

    let chartTuple = this.getChartById(idChart);
    // El chart que Chart.js tiene asociado ahora mismo a ese canvas (si lo hay).
    let chartOnCanvas = Chart.getChart(idChart);

    // Al recargar las zonas Angular recrea el canvas, dejando el chart anterior
    // apuntando a un elemento que ya no está en el DOM: hay que destruirlo.
    if (chartTuple != undefined && chartTuple.chart != chartOnCanvas) {
      chartTuple.chart.destroy();
    }

    let chartData = {
      labels: labels,
      datasets: [{
        label: this.localiser.get("reports.whConsumptionGraph"),
        data: data,
        backgroundColor: 'rgba(24, 38, 79, 0.25)',
        borderColor: 'rgba(24, 38, 79, 1)',
        borderWidth: 1,
        stack: 'combined',
        fill: true,
      }]
    };

    let chart;
    if (chartOnCanvas != undefined) {
      chart = chartOnCanvas;
      chart.data = chartData;
      chart.update();
    } else {
      chart = new Chart(idChart, {
        type: 'line',
        data: chartData,
        options: {
          maintainAspectRatio: true,
          scales: {
            y: {
              beginAtZero: true
            }
          }
        }
      });
    }

    if (chartTuple == undefined) {
      this.charts.push({id: idChart, chart: chart});
    } else {
      chartTuple.chart = chart;
    }
  }

  getChartById(idChart) {
    for (let tuple of this.charts) {
      if (tuple.id == idChart) {
        return tuple;
      }
    }
    return undefined;
  }

  getChecks() {
    this.track(this.user.getChecks("-1", new Array<string>("monthlyReportCheck")).subscribe(result => {
      let jsonResult: JSON = JSON.parse(result);
      this.monthlyReportCheck = jsonResult["monthlyReportCheck"];
    }));
  }

  setCheck(event) {
    this.user.setCheck("-1", event.source.id, event.checked).subscribe(result => { })
  }

  openConfigure() {
    let dialog = this.matDialog.open(ConfigureComponent, {
      width: '70%',
      height: '85%',
      data: { factors: { ...this.factors }}
    });

    dialog.afterClosed().subscribe(data => {
      if (data != undefined) {
        this.factors = data.factors;
        this.consumoService.newFactoresConsumo(this.factors, this.user.getId()).subscribe(result => { })
      }
    });
  }

  selectionForPrint() {
    let dialog = this.matDialog.open(SelectionPrintComponent, {
      width: '60%',
      height: '70%',
      data: {proyectos: this.user.getProyectos(), zonas: this.zonas}
    });

    dialog.afterClosed().subscribe(data => {
      if (data != undefined && data.action) {
        this.print(data.selected)
      }
    });
  }

  getProyectoToPrint(proyecto: proyecto) {
    return `

        <div class="salto_pagina_despues">
          <div class="row titleSectionP">
            <div class="titleHeaderP">
              <h2 class="textL centerVert" style="text-align: center">` + this.localiser.get("devices.project") + ` - ` + proyecto.nombre + `</h2>
            </div>
          </div>

          <div class="row">
            <div class="col-4">

              <div style="height: 50%" class="row">
                <div style="text-align: center" class="col marginAuto">
                  <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">bolt</span>
                  <h3 class="textM">` + this.localiser.get("reports.totalConsumption") + `:<br><b>` + this.consumoSumaProyecto(proyecto)[2].toFixed(2) + ` kWh</b></h3>
                </div>
                <div style="text-align: center" class="col marginAuto">
                  <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">history</span>
                  <h3 class="textM">` + this.localiser.get("reports.average") + `:<br><b>` + this.getMediaDiariaOfConsumoTotalByProyecto(proyecto) + ` kWh</b></h3>
                </div>
              </div>
              <div style="height: 50%" class="row">
                <div style="text-align: center" class="col marginAuto">
                  <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">today</span>
                  <h3 class="textM">` + this.localiser.get("reports.dayWithMoreConsumption") + `:<br><b>` + this.getDiaMasConsumoByProyecto(proyecto) + `</b></h3>
                </div>
                <div style="text-align: center" class="col marginAuto">
                  <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">cloud_circle</span>
                  <h3 class="textM">` + this.localiser.get("reports.co2Emissions") + `:<br><b>` + this.getConsumoCO2ByProyecto(proyecto) + ` ` + this.localiser.get("reports.metricTons") + `</b></h3>
                </div>
              </div>

            </div>

            <div class="col-8">
              <img class="centerVert" style="width: 100%" src="`
                + this.getChartById("canvasId-" + proyecto.idProyecto).chart.toBase64Image() + `"/>
            </div>

          </div>

          <div class="row" style="margin-top: 2vh; text-align: center">
              <h3 class="textXL marginAuto" style="width: 25%; border-bottom: 2px solid #18264f">` + this.localiser.get("reports.saving") + `</h3>
          </div>
          <div class="row" style="margin-top: 2vh; text-align: center">
              <div style="text-align: center" class="col marginAuto">
                <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">flash_off</span>
                <h3 class="textXM">` + this.localiser.get("reports.totalConsumptionSaved") + `:<br><b>` + this.getAhorroConsumoByProyecto(proyecto).toFixed(2) + ` kWh</b></h3>
              </div>
              <div style="text-align: center" class="col marginAuto">
                <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">cloud_off</span>
                <h3 class="textXM">` + this.localiser.get("reports.co2EmissionsAvoided") + `:<br><b>` + this.getAhorroCO2ByProyecto(proyecto) + ` ` + this.localiser.get("reports.metricTons") + `</b></h3>
              </div>
              <div style="text-align: center" class="col marginAuto">
                <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">directions_car</span>
                <h3 class="textXM">` + this.localiser.get("reports.carGasolineKilometers") + `:<br><b>` + this.getKilometrosCocheByProyecto(proyecto) + ` ` + this.localiser.get("reports.kilometers") + `</b></h3>
              </div>
              <div style="text-align: center" class="col marginAuto">
                <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">home</span>
                <h3 class="textXM">` + this.localiser.get("reports.houseEnergyUse") + `:<br><b>` + this.getEnergiaCasaByProyecto(proyecto) + ` ` + this.localiser.get("reports.years") + `</b></h3>
              </div>
              <div style="text-align: center" class="col marginAuto">
                <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">park</span>
                <h3 class="textXM">` + this.localiser.get("reports.plantedTrees") + `:<br><b>` + this.getArbolesPlantadosByProyecto(proyecto) + ` ` + this.localiser.get("reports.trees") + `</b></h3>
              </div>
              <div style="text-align: center" class="col marginAuto">
                <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">charging_station</span>
                <h3 class="textXM">` + this.localiser.get("reports.chargedSmartphones") + `:<br><b>` + this.getMovilesCargadosByProyecto(proyecto) + ` ` + this.localiser.get("reports.smartphones") + `</b></h3>
              </div>
          </div>
        `;

  }

  getZonaToPrint(zona) {
    return `

            <div class="col-6" style="height: 100%; margin: auto">
              <div class="row zoneTitleSectionP">
                <div class="zoneTitleHeaderP">
                  <h2 class="textXM centerVert" style="text-align: center">` + this.localiser.get("devices.zone") + ` - ` + zona.nombre + `</h2>
                 </div>
              </div>
              <div style="text-align: center">
                <img style="width: 90%; "
                  src="` + this.getChartById("canvasId-" + zona.id).chart.toBase64Image() + `"/>

                <div style="height: 50%; margin-top: 5%" class="row">
                  <div style="text-align: center" class="col marginAuto">
                    <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">bolt</span>
                    <h3 class="textM">` + this.localiser.get("reports.totalConsumption") + `:<br><b>` + this.getConsumoTotalByZona(zona).toFixed(2) + ` kWh</b></h3>
                  </div>
                  <div style="text-align: center" class="col marginAuto">
                    <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">history</span>
                    <h3 class="textM">` + this.localiser.get("reports.average") + `:<br><b>` + this.getMediaDiariaOfConsumoTotal(zona) + ` kWh</b></h3>
                  </div>
                </div>
                <div style="height: 50%" class="row">
                  <div style="text-align: center" class="col marginAuto">
                    <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">today</span>
                    <h3 class="textM">` + this.localiser.get("reports.dayWithMoreConsumption") + `:<br><b>` + this.getDiaMasConsumoByZona(zona) + `</b></h3>
                  </div>
                  <div style="text-align: center" class="col marginAuto">
                    <span style="display: contents" class="material-icons-outlined customRowIcon textXXL">cloud_circle</span>
                    <h3 class="textM">` + this.localiser.get("reports.co2Emissions") + `:<br><b>` + this.getConsumoCO2ByZona(zona) + ` ` + this.localiser.get("reports.metricTons") + `</b></h3>
                  </div>
                </div>

              </div>
            </div>

    `;
  }
  print(selecteds: selected[]) {
    let popupWin;
    popupWin = window.open("", "_blank", "top=0,left=0,height=100%,width=auto");
    popupWin.document.open();

    let printHeader = `<h3 style="text-align: center; border-bottom: 2px solid #18264f" class="textXL">` + this.localiser.get("reports.PDFTitle") + ` ` + this.localiser.get(this.months[this.monthSelected]) + ` ` + this.getYearSelected() + `</h3>`;

    let printContents = [];

    for (let selected of selecteds) {

      let printContent = this.getProyectoToPrint(selected.proyecto);

      for (let i = 0; i < selected.zonas.length; i++) {
        let printContentZona = "";
        if (i == 0) {
          printContentZona = `<div class="row salto_pagina_despues" style="margin: 2vh auto">`
        } else if (i % 2 == 0) {
          printContentZona = `<div class="row" style="margin: 2vh auto">`
        }
        printContentZona += this.getZonaToPrint(selected.zonas[i]);
        if (i % 2 == 1 || i == selected.zonas.length - 1) {
          printContentZona += `</div>`
        }
        printContent = printContent + printContentZona;
      }


      printContents.push(printContent);

    }

    let print = printHeader + printContents;

    popupWin.document.write(`
      <html>
        <head>
          <title>Reporte</title>
          <meta name="viewport" content="width=10000, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
          <link rel="stylesheet"
          href="https://stackpath.bootstrapcdn.com/bootstrap/4.3.1/css/bootstrap.min.css"
          integrity="sha384-ggOyR0iXCbMQv3Xipma34MD+dH/1fQ784/j6cY/iJTQUOhcWr7x9JvoRxT2MZw1T" crossorigin="anonymous">
          <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined" rel="stylesheet" />
          <link rel="stylesheet" href="styles.css">
          <style>
            .salto_pagina_despues{
              page-break-after:always;
            }

            .salto_pagina_anterior{
              page-break-before:always;
            }

            .content {
              height: 100vh;
              width: 100%;
              display: flex;
              flex-direction: column;
            }

            .img-content {
              flex: 1;
              display: flex;
              justify-content: center;
              align-items: center;
            }

            .observation {
              height: 150px;
              overflow: hidden;
              overflow-y: auto;
            }
          </style>
        </head>
        <body onload="window.print();">
          ${print}
          <img class="PDFHeart" src="../../../assets/img/PDF_logo.png" alt="Atenea PDF logo">
        </body>
      </html>`);
    /* window.close(); */
    popupWin.document.close();
  }

}
